/**
 * @file LoginUsingEmaild.jsx
 * @description This file contains the LoginUsingEmaild component which handles user login via email.
 * It includes functionality for OTP login, password login, and guest login options.
 * It also provides a user interface for entering email, OTP and password.
 */

import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
    Button,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    Tooltip,
    FormLabel,
    tooltipClasses
} from '@mui/material';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import OtpInput from 'react18-input-otp';
import useLoginUser from './hooks/useLoginUser';
import useAuthNavigation from '../useAuthNavigation';
import { H3, Body2, Body3 } from '../../../themes/typography';
import '../../../styles/components/LoginForm.css';

export default function LoginUsingEmaild() {
    const { showGuestLoginForm } = useSelector((state) => state.authDialog);
    const { loading } = useSelector((state) => state.auth);
    const {
        formik,
        showPassword,
        activeLoginMethod,
        backToOptions,
        setShowPassword,
        handleOtpLogin,
        handlePasswordLogin,
        getSlideClass,
        signInWithPassword,
        resendOtpEmail,
        resendLoading,
        seconds,
        loginWithOtp,
        getLoginMessage,
        isTooManyRequests
    } = useLoginUser();
    const { showVerifyEmailForm, showGuestLogin, showForgotPasswordForm } = useAuthNavigation();

    return (
        <React.Fragment>
            <div className="main-login-email-collect-container">
                <div className="login-welcome-greetings">
                    <H3 color="var(--secondary-color)" fontWeight={700}>
                        {activeLoginMethod === 'forgotPassword'
                            ? 'Reset Password'
                            : 'Hi, Welcome Back!'}
                    </H3>
                    <Body2 color="var(--tertiary-color)">{getLoginMessage()}</Body2>
                </div>

                <form className="main-login-form" onSubmit={formik.handleSubmit} autoComplete="off">
                    {/* Main Component */}
                    <div
                        className={`different-login-forms slide-transition ${getSlideClass('login')}`}
                    >
                        <div className="main-switchable-login-form">
                            <div className="switchable-login-input-container">
                                <TextField
                                    fullWidth
                                    id="email"
                                    name="email"
                                    label="Email"
                                    type="email"
                                    disabled={loading}
                                    value={formik.values.email}
                                    onChange={(e) =>
                                        formik.setFieldValue('email', e.target.value.trim())
                                    }
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                />
                            </div>

                            <div className="login-methods-container">
                                <div className="login-methods-action-buttons">
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="tertiaryMain"
                                        onClick={handleOtpLogin}
                                        disabled={formik.values.isOtpLogin && loading}
                                        disableRipple
                                    >
                                        OTP Login{' '}
                                        {formik.values.isOtpLogin && loading && (
                                            <CircularProgress
                                                size={20}
                                                sx={{
                                                    marginLeft: '0.75rem',
                                                    color: 'var(--tertiary-color)'
                                                }}
                                            />
                                        )}
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="main"
                                        type="submit"
                                        onClick={handlePasswordLogin}
                                        disabled={!formik.values.isOtpLogin && loading}
                                    >
                                        Login with Password{' '}
                                        {!formik.values.isOtpLogin && loading && (
                                            <CircularProgress
                                                size={20}
                                                sx={{
                                                    marginLeft: '0.75rem',
                                                    color: 'var(--primary-color)'
                                                }}
                                            />
                                        )}
                                    </Button>
                                </div>
                                <div
                                    className="dont-have-account-with-guest-login"
                                    style={{
                                        justifyContent: showGuestLoginForm
                                            ? 'space-between'
                                            : 'center'
                                    }}
                                >
                                    <div className="dont-have-login-account">
                                        <Body3>Don't have an account?</Body3>
                                        <Button
                                            sx={guestLoginBtnStyle}
                                            onClick={showVerifyEmailForm}
                                            disableRipple
                                        >
                                            Sign up
                                        </Button>
                                    </div>
                                    {showGuestLoginForm && (
                                        <div className="guest-login-option">
                                            <Button
                                                sx={guestLoginBtnStyle}
                                                onClick={showGuestLogin}
                                            >
                                                Guest Login
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Email Login */}
                    <div
                        className={`send-login-method-container slide-transition ${getSlideClass('otpLogin')}`}
                    >
                        <div className="main-switchable-login-form">
                            <div className="switchable-login-input-container">
                                <FormLabel>
                                    <Body2 color="var(--secondary-color)">
                                        Enter the OTP sent to your email
                                    </Body2>
                                </FormLabel>
                                <div
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.5rem'
                                    }}
                                >
                                    <OtpInput
                                        id="otp"
                                        name="otp"
                                        isInputNum
                                        // isInputSecure
                                        // isDisabled
                                        value={formik.values.otp}
                                        onChange={(otpNumber) =>
                                            formik.setFieldValue('otp', otpNumber)
                                        }
                                        numInputs={6}
                                        inputStyle={otpInputStyle}
                                        containerStyle={{
                                            justifyContent: 'space-between',
                                            gap: '1rem'
                                        }}
                                        focusStyle={{
                                            outline: 'none',
                                            border: `2px solid var(--secondary-color)`
                                        }}
                                        errorStyle={{ border: `2px solid var(--error-main-color)` }}
                                        hasErrored={
                                            formik.touched.otp && Boolean(formik.errors.otp)
                                        }
                                    />
                                    <Body3 color="var(--error-main-color)" fontWeight={300}>
                                        {formik.touched.otp && formik.errors.otp}
                                    </Body3>
                                </div>
                                <Tooltip
                                    title="If you haven't received an email, please check your spam folder."
                                    placement="bottom-start"
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                maxWidth: '332px',
                                                fontWeight: '400',
                                                fontFamily: 'var(--secondary-content-font-family)',
                                                color: 'var(--primary-color)',
                                                backgroundColor: 'var(--quaternary-color)',
                                                margin: '0',
                                                fontSize: '0.8rem'
                                            }
                                        },
                                        popper: {
                                            sx: {
                                                [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                                                {
                                                    marginTop: '0.25rem'
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <div className="dont-have-login-account">
                                        <Body3>Didn't receive an email?</Body3>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0rem'
                                            }}
                                        >
                                            <Button
                                                onClick={resendOtpEmail}
                                                disabled={resendLoading || seconds > 0}
                                                sx={{
                                                    ...guestLoginBtnStyle,
                                                    '&.Mui-disabled': {
                                                        background: 'transparent',
                                                        color: '#26c1e8'
                                                    }
                                                }}
                                            >
                                                Resend
                                            </Button>
                                            {seconds > 0 && (
                                                <Body3
                                                    color="var(--tertiary-color)"
                                                    fontWeight={500}
                                                >
                                                    {`00:${seconds < 10 ? `0${seconds}` : seconds}`}
                                                </Body3>
                                            )}
                                        </div>
                                    </div>
                                </Tooltip>
                            </div>

                            <div className="login-methods-container">
                                <div className="login-methods-action-buttons">
                                    <Button
                                        variant="main"
                                        fullWidth
                                        type="submit"
                                        disabled={loading || isTooManyRequests}
                                        onClick={loginWithOtp}
                                    >
                                        Continue{' '}
                                        {loading && (
                                            <CircularProgress
                                                size={20}
                                                sx={{
                                                    marginLeft: '0.75rem',
                                                    color: 'var(--primary-color)'
                                                }}
                                            />
                                        )}
                                    </Button>
                                    <div className="guest-login-option">
                                        <Button
                                            onClick={backToOptions}
                                            disableRipple
                                            sx={backToOption}
                                        >
                                            Back to Login
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Password Login */}
                    <div
                        className={`password-login-method-container slide-transition ${getSlideClass('passwordLogin')}`}
                    >
                        <div className="main-switchable-login-form">
                            <div className="password-login-input-container">
                                <TextField
                                    fullWidth
                                    id="password"
                                    name="password"
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formik.values.password}
                                    disabled={loading}
                                    onChange={(e) =>
                                        formik.setFieldValue('password', e.target.value)
                                    }
                                    error={
                                        formik.touched.password && Boolean(formik.errors.password)
                                    }
                                    helperText={formik.touched.password && formik.errors.password}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() =>
                                                            setShowPassword(!showPassword)
                                                        }
                                                    >
                                                        {showPassword ? (
                                                            <Visibility color="white" />
                                                        ) : (
                                                            <VisibilityOff color="white" />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }
                                    }}
                                />
                                <Button
                                    color="secondary"
                                    sx={resetBtnStyle}
                                    disableRipple
                                    onClick={showForgotPasswordForm}
                                >
                                    Forgot Password?
                                </Button>
                            </div>

                            <div className="login-methods-container">
                                <div className="login-methods-action-buttons">
                                    <Button
                                        variant="main"
                                        fullWidth
                                        disabled={loading}
                                        type="submit"
                                        onClick={signInWithPassword}
                                    >
                                        Sign In{' '}
                                        {loading && (
                                            <CircularProgress
                                                size={20}
                                                sx={{
                                                    marginLeft: '0.75rem',
                                                    color: 'var(--primary-color)'
                                                }}
                                            />
                                        )}
                                    </Button>
                                    <Button disableRipple sx={backToOption} onClick={backToOptions}>
                                        Back to options
                                    </Button>
                                </div>
                                {/* <div className='dont-have-login-account' style={{ justifyContent: 'center', paddingTop: '0.75rem', borderTop: `1px solid var(--divider-color)` }}>
                                    <Body3>
                                        Don't have an account?
                                    </Body3>
                                    <Button sx={guestLoginBtnStyle} disableRipple onClick={showVerifyEmailForm}>
                                        Sign up
                                    </Button>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </form>

                <div className="login-terms-agree-container">
                    <Body3>
                        By signing in you accept Shmooz {''}
                        <span className="terms">
                            <Link>Terms of Service</Link>
                        </span>{' '}
                        and
                        <span className="terms">
                            {' '}
                            {''}
                            <Link>Privacy Policy</Link>
                        </span>
                    </Body3>
                </div>
            </div>
        </React.Fragment>
    );
}

const resetBtnStyle = {
    padding: '0',
    fontSize: '0.875rem'
};

const guestLoginBtnStyle = {
    padding: '0',
    fontSize: '0.875rem',
    color: 'var(--tertiary-color)',
    fontWeight: '500',
    fontFamily: 'var(--secondary-content-font-family)'
};

const backToOption = {
    padding: '0',
    fontSize: '0.875rem',
    color: '#ccc',
    '&:hover': {
        textDecoration: 'underline'
    }
};

const otpInputStyle = {
    width: '100%',
    margin: '0px',
    padding: '12px',
    color: '#f5f5f5',
    border: `1px solid #f5f5f5`,
    backgroundColor: 'transparent',
    fontWeight: 500,
    fontSize: '1rem',
    borderRadius: '10px'
};
